import { useContext } from 'react';
import get from 'lodash/get';
import omit from 'lodash/omit';
import { useDispatch } from 'react-redux';

import communicationQueuesResource from 'src/actions/communicationQueues/communicationQueuesResource';
import { getCleanedConfig, translate } from 'src/presentation/components/pages/chat/helpers/chatUtils';
import { ChatContext } from 'src/presentation/components/pages/chat/ChatConfigurations';
import { validateThrottling } from 'src/presentation/components/pages/chat/helpers/validateThrottling';
import generateId from 'src/lib/generateId';
import { CHAT_BRAND } from 'src/constants/branding';

const SAVED_TOAST_MESSAGE = 'Saved! Your changes may take up to 5 minutes to be reflected on the site.';
const CREATED_TOAST_MESSAGE = `Success! You have added a new ${CHAT_BRAND.name}.`;

export function useOnSave(editorType: string | null, isMobile = false) {
  const dispatch = useDispatch();
  const { accountId, afterSave } = useContext(ChatContext);

  return async ({ add, resetErrors, validate, update, workingState }) => {
    dispatch(resetErrors());

    // Cleans and formats the workingState before saving
    // Sets all hidden fields to defaults, formats onboarding messages
    const cleanedConfig = omit(getCleanedConfig(workingState), ['inboxes']);

    const endpoint = translate(cleanedConfig, { reverse: true });

    if (editorType === 'throttling') {
      if (!dispatch(validateThrottling(get(cleanedConfig, 'configuration.throttling')))) {
        return;
      }
    } else {
      const newEndpoint = isMobile ? omit(endpoint, ['configuration.selfService.quickActions']) : endpoint;
      const isError = !(await dispatch(validate(newEndpoint)));
      if (isError) {
        return;
      }
    }
    // duplicate colors from theme to configuration.colors
    cleanedConfig.configuration.colors = cleanedConfig.configuration.theme.colors;

    if (endpoint.id) {
      if (!endpoint.configuration?.integrationId) {
        endpoint.configuration = {
          ...endpoint.configuration,
        };
      }

      dispatch(update({ id: endpoint.id }, endpoint, { customToastMsg: SAVED_TOAST_MESSAGE }));
    } else {
      endpoint.id = generateId();

      endpoint.configuration = {
        ...endpoint.configuration,
        providerId: 'CHAT_ROCKET',
        provider: 'CHAT_ROCKET',
      };

      await dispatch(
        add(
          {},
          {
            accountId: accountId,
            ...endpoint,
          },
          { disableWindowBack: true, customToastMsg: null }
        )
      );

      dispatch(
        communicationQueuesResource.effects.createNewCommunicationQueue(endpoint, {
          customToastMsg: CREATED_TOAST_MESSAGE,
        })
      );

      afterSave && afterSave(endpoint);
    }
  };
}
